import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export const EmailLogTab = () => {
  const [data, setData] = useState({ rows: [], counts: {} });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [copies, setCopies] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/email-log", { params: { status: status || undefined, q: q || undefined, include_copies: copies } })
    .then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [status, q, copies]); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async (r) => {
    setBusy(r.id);
    try { await api.post(`/admin/email-log/${r.id}/resend`); toast.success(`Email resent to ${r.to}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); load(); }
  };

  return (
    <div data-testid="admin-email-log-tab">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[["", "All"], ["accepted", `Sent (${data.counts.accepted ?? 0})`], ["failed", `Failed (${data.counts.failed ?? 0})`]].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} className={`chip text-xs ${status === k ? "bg-[var(--navy)] text-white border-[var(--navy)]" : ""}`} data-testid={`email-filter-${k || "all"}`}>{l}</button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient or subject" className="flex-1 min-w-[180px] p-2 text-sm border border-[var(--border-warm)] rounded-full px-4" data-testid="email-search"/>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={copies} onChange={(e) => setCopies(e.target.checked)} data-testid="email-include-copies"/> Show copies
        </label>
      </div>
      {data.rows.length === 0 ? (
        <div className="card-warm p-6 text-sm text-zinc-500" data-testid="email-empty">No emails in this view yet.</div>
      ) : (
        <div className="card-warm divide-y divide-[var(--border-warm)]">
          {data.rows.map((r) => (
            <div key={r.id} className="p-3 flex items-start gap-3 flex-wrap" data-testid={`email-row-${r.id}`}>
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 ${r.status === "failed" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                {r.status === "failed" ? "Failed" : "Sent"}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{r.subject}</div>
                <div className="text-xs text-zinc-600 break-all">To {r.to} · {new Date(r.created_at).toLocaleString()}</div>
                {r.error && <div className="text-xs text-red-700 mt-1 break-words">{r.error.slice(0, 220)}</div>}
                {r.resent_at && <div className="text-xs text-zinc-500 mt-1">Resent {new Date(r.resent_at).toLocaleString()} {r.resent_ok ? "· accepted" : "· failed again"}</div>}
              </div>
              {r.can_resend && (
                <button onClick={() => resend(r)} disabled={busy === r.id} className="chip text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid={`email-resend-${r.id}`}>
                  <RotateCw size={12} className={busy === r.id ? "animate-spin" : ""}/> {busy === r.id ? "Sending…" : "Resend"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-zinc-500 mt-3">"Sent" means the email service accepted the message for delivery. Showing the latest 300 emails.</p>
    </div>
  );
};
